import React, { useState } from 'react';
import {
  Building2, Users, Mail, CheckCircle, Star, Sparkles, MessageCircle, X,
  ExternalLink, ArrowRight, Award, ChevronRight, Target, Zap, Globe,
  Heart, Shield, Send
} from 'lucide-react';
import { companyInfo, contactInfo } from '../mock';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import PortfolioShowcase from '../components/PortfolioShowcase';
import { toast } from 'sonner';

const HomePage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Message sent! We\'ll be in touch soon.');
    setFormData({ name: '', email: '', message: '' });
  };

  const WHY_US = [
    { icon: Star, title: 'Proven Track Record', desc: 'Years of successful management across multiple industry-leading groups', color: '#F59E0B' },
    { icon: Users, title: 'Expert Leadership', desc: 'A dedicated team bringing diverse expertise and passion to every project', color: '#00E5FF' },
    { icon: Target, title: 'Quality Focused', desc: 'We maintain the highest standards across all operations, never compromising', color: '#10B981' },
    { icon: Sparkles, title: 'Innovation Driven', desc: 'Constantly evolving with new strategies to stay ahead of industry trends', color: '#8A2BE2' },
    { icon: Building2, title: 'Diverse Portfolio', desc: 'From hospitality to retail, education to food service — we excel everywhere', color: '#3B82F6' },
    { icon: Heart, title: 'Community First', desc: 'Building strong, engaged communities that thrive and grow together', color: '#EF4444' },
  ];

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="home-page">

      {/* ═══ HERO ═══ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background image + overlay */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center" style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_starnight-group/artifacts/kjt0zh20_theroyaloak.png)',
            filter: 'blur(12px) brightness(0.3)', transform: 'scale(1.15)'
          }} />
          <div className="absolute inset-0 bg-[#05050A]/60" />
        </div>
        {/* Ambient glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[700px] h-[700px] bg-[#00E5FF]/[0.04] rounded-full blur-[180px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#8A2BE2]/[0.04] rounded-full blur-[140px]" />
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md mb-8"
            style={{ animation: 'fadeInUp 0.6s ease-out' }}>
            <Sparkles className="w-3.5 h-3.5 text-[#00E5FF]" />
            <span className="text-xs tracking-[0.2em] uppercase text-white/50 font-medium">Welcome to Starnight</span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-white mb-6 leading-[0.95]"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.1s both' }}>
            {companyInfo.name.split(' ')[0]}{' '}
            <span className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent font-medium">
              {companyInfo.name.split(' ')[1]}
            </span>
          </h1>

          <p className="text-lg md:text-xl text-white/35 max-w-xl mx-auto leading-relaxed mb-10"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.2s both' }}>
            We don't just manage groups — we elevate them.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.3s both' }}>
            <Button onClick={() => window.open(contactInfo.discord, '_blank')} data-testid="hero-discord-btn"
              className="bg-white text-black hover:bg-white/90 px-7 py-3 text-sm font-medium rounded-lg transition-all group">
              <MessageCircle className="w-4 h-4 mr-2" />
              Join Our Discord
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
            </Button>
            <Button onClick={() => document.getElementById('groups').scrollIntoView({ behavior: 'smooth' })} data-testid="hero-explore-btn"
              className="bg-white/5 text-white hover:bg-white/10 border border-white/10 px-7 py-3 text-sm font-medium rounded-lg transition-all backdrop-blur-sm">
              Explore Our Groups
            </Button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2" style={{ animation: 'fadeInUp 0.6s ease-out 0.5s both' }}>
          <div className="w-5 h-9 border border-white/15 rounded-full flex items-start justify-center p-1.5">
            <div className="w-1 h-2 bg-white/30 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* ═══ STATS BAR ═══ */}
      <section className="py-16 border-y border-white/[0.03]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '5+', label: 'Active Groups' },
              { value: '1000+', label: 'Community Members' },
              { value: '3+', label: 'Years Experience' },
              { value: '100%', label: 'Commitment' },
            ].map((stat, i) => (
              <div key={i} className="text-center group" style={{ animation: `fadeInUp 0.4s ease-out ${0.1 * i}s both` }}
                data-testid={`hero-stat-${i}`}>
                <p className="text-3xl md:text-4xl font-light bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent mb-1">
                  {stat.value}
                </p>
                <p className="text-[10px] tracking-[0.15em] uppercase text-white/25">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ABOUT ═══ */}
      <section id="about" className="py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] mb-6">
                <Globe className="w-3 h-3 text-[#00E5FF]" />
                <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium">About Us</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-light tracking-tight text-white mb-6 leading-tight">
                Excellence in{' '}
                <span className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent font-medium">
                  Virtual Hospitality
                </span>
              </h2>
              <p className="text-white/30 text-sm leading-relaxed mb-4">{companyInfo.description}</p>
              <p className="text-white/30 text-sm leading-relaxed">{companyInfo.mission}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {companyInfo.values.map((value, index) => (
                <div key={index} data-testid={`value-card-${index}`}
                  className="group p-5 rounded-xl border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.03] transition-all hover:-translate-y-0.5"
                  style={{ animation: `fadeInUp 0.4s ease-out ${0.08 * index}s both` }}>
                  <CheckCircle className="w-5 h-5 text-[#00E5FF] mb-3 group-hover:scale-110 transition-transform" />
                  <p className="text-white text-sm font-medium tracking-tight">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ GROUPS / PORTFOLIO ═══ */}
      <PortfolioShowcase />

      {/* ═══ COMMUNITY / DISCORD CTA ═══ */}
      <section className="py-20 relative overflow-hidden" data-testid="community-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-[#5865F2]/[0.03] via-transparent to-transparent" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#5865F2]/[0.04] rounded-full blur-[150px]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#5865F2]/20 bg-[#5865F2]/[0.05] backdrop-blur-md mb-6"
            style={{ animation: 'fadeInUp 0.6s ease-out' }}>
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-[#5865F2] fill-current"><path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z"/></svg>
            <span className="text-xs tracking-[0.15em] uppercase text-[#5865F2]/80 font-medium">Community</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-light tracking-tight text-white mb-4 leading-tight"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.1s both' }}>
            Join Our <span className="text-[#5865F2] font-medium">Discord</span> Community
          </h2>
          <p className="text-base text-white/30 max-w-lg mx-auto leading-relaxed mb-8"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.2s both' }}>
            Connect with our team, stay updated with announcements, and be part of our growing community.
          </p>
          <div className="flex items-center justify-center gap-4" style={{ animation: 'fadeInUp 0.6s ease-out 0.3s both' }}>
            <Button onClick={() => window.open('https://discord.gg/starnightholdings', '_blank')} data-testid="join-discord-btn"
              className="bg-[#5865F2] hover:bg-[#4752C4] text-white px-8 py-3 text-sm font-medium rounded-xl transition-all group">
              <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2 fill-current"><path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z"/></svg>
              Join Discord
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </div>
          <div className="flex items-center justify-center gap-6 mt-8 text-white/15 text-xs">
            <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />100+ Members</span>
            <span className="flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" />Active Community</span>
            <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" />Verified Server</span>
          </div>
        </div>
      </section>

      {/* ═══ WHY CHOOSE US ═══ */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] mb-5">
              <Award className="w-3 h-3 text-[#8A2BE2]" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium">Why Choose Us</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white mb-3">
              What Sets Us <span className="text-[#8A2BE2]">Apart</span>
            </h2>
            <p className="text-white/25 text-sm max-w-md mx-auto">
              Experience the difference of working with a holdings company that truly cares
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHY_US.map((item, index) => (
              <div key={index} data-testid={`why-card-${index}`}
                className="group p-6 rounded-xl border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.03] transition-all duration-300 hover:-translate-y-0.5"
                style={{ animation: `fadeInUp 0.4s ease-out ${0.06 * index}s both` }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 border border-white/[0.06] group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: `${item.color}10` }}>
                  <item.icon className="w-5 h-5" style={{ color: item.color }} />
                </div>
                <h3 className="text-white font-medium text-sm mb-1.5 tracking-tight">{item.title}</h3>
                <p className="text-white/20 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CONTACT ═══ */}
      <section id="contact" className="py-24 relative">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#00E5FF]/[0.02] rounded-full blur-[120px]" />
        </div>

        <div className="relative z-10 max-w-2xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] mb-5">
              <Mail className="w-3 h-3 text-[#00E5FF]" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium">Get in Touch</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white mb-3">
              Contact <span className="text-[#00E5FF]">Us</span>
            </h2>
            <p className="text-white/25 text-sm">Have questions? We'd love to hear from you.</p>
          </div>

          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-8" data-testid="contact-form">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-[11px] text-white/30 mb-2 uppercase tracking-wider font-medium">Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required
                  data-testid="contact-name-input" placeholder="Your name"
                  className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-[#00E5FF]/40 focus:border-[#00E5FF]/30 transition-all text-sm" />
              </div>
              <div>
                <label className="block text-[11px] text-white/30 mb-2 uppercase tracking-wider font-medium">Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required
                  data-testid="contact-email-input" placeholder="you@example.com"
                  className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-[#00E5FF]/40 focus:border-[#00E5FF]/30 transition-all text-sm" />
              </div>
              <div>
                <label className="block text-[11px] text-white/30 mb-2 uppercase tracking-wider font-medium">Message</label>
                <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required rows="4"
                  data-testid="contact-message-input" placeholder="Tell us what you need..."
                  className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-[#00E5FF]/40 focus:border-[#00E5FF]/30 transition-all text-sm resize-none" />
              </div>
              <Button type="submit" data-testid="contact-submit-btn"
                className="w-full bg-white text-black hover:bg-white/90 font-medium py-3 rounded-xl text-sm transition-all group">
                <Send className="w-4 h-4 mr-2" />
                Send Message
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/[0.04] text-center">
              <a href={`mailto:${contactInfo.generalEmail}`} data-testid="contact-email-link"
                className="inline-flex items-center gap-2 text-white/30 hover:text-[#00E5FF] text-xs transition-colors">
                <Mail className="w-3.5 h-3.5" />{contactInfo.generalEmail}
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default HomePage;
